import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  CheckCircle,
  Circle,
  ShieldCheck,
  Eye,
  MagnifyingGlass,
  Envelope,
  ChatCircleDots,
  Target,
  ArrowSquareOut,
  ArrowClockwise,
  Rocket,
} from "@phosphor-icons/react";

/*
  VA Onboarding — a single "Delegate to VA" checklist that walks a new
  read-only admin through everything they need to run cold outreach.
  Steps IDs must match backend routers/va_onboarding.py::STEP_IDS.
*/
const STEPS = [
  {
    id: "read_safety_rules",
    icon: ShieldCheck,
    title: "Read the 5 safety rules",
    time: "3 min",
    body: (
      <ul className="text-sm text-neutral-300 space-y-2 list-disc pl-5 mt-3">
        <li>Never send more than 20 cold emails per day per inbox — sender reputation dies fast above that.</li>
        <li>Never edit or forward customer data (leads' phone numbers, MC#s) to a third-party tool.</li>
        <li>If a lead replies with "STOP", "Remove me", or a legal threat — mark unsubscribed IMMEDIATELY.</li>
        <li>Only use pre-approved templates. Never freestyle a first-touch email.</li>
        <li>If you're unsure — escalate to the founder via Slack. Better a slow reply than a wrong one.</li>
      </ul>
    ),
    cta: null,
  },
  {
    id: "verify_readonly_access",
    icon: Eye,
    title: "Confirm your read-only access works",
    time: "1 min",
    body: (
      <p className="text-sm text-neutral-300 mt-3">
        Open the Cold Leads dashboard. You should see the full table but the "Send"
        and "Import FMCSA" buttons should be disabled (grey/lock icon). If you can send — <strong>stop and tell the founder</strong>, your role is misconfigured.
      </p>
    ),
    cta: { to: "/app/admin/leads", label: "Open Cold Leads →" },
  },
  {
    id: "review_10_leads",
    icon: MagnifyingGlass,
    title: "Review 10 leads (read-only)",
    time: "10 min",
    body: (
      <p className="text-sm text-neutral-300 mt-3">
        Click into 10 lead rows. For each one, note in your head: does this
        broker look active? MC number valid? Any bounce or unsubscribe flag? This
        trains your eye for the "worth-emailing" pattern.
      </p>
    ),
    cta: { to: "/app/admin/leads", label: "Go review leads →" },
  },
  {
    id: "browse_templates",
    icon: Envelope,
    title: "Skim the approved email templates",
    time: "5 min",
    body: (
      <p className="text-sm text-neutral-300 mt-3">
        Read all templates end-to-end. These are the ONLY messages you're
        cleared to send. If a template is missing for a scenario, ask the
        founder — don't improvise.
      </p>
    ),
    cta: { to: "/app/admin/reply-templates", label: "Open templates →" },
  },
  {
    id: "reply_first_hot_lead",
    icon: ChatCircleDots,
    title: "Send your first hot-lead reply",
    time: "5 min",
    body: (
      <p className="text-sm text-neutral-300 mt-3">
        Hot leads (🔥) are prospects who already replied to our outreach —
        replying to them is your #1 daily job. Open the Hot Replies strip on
        the Cold Leads page, pick one, use the "Reply-Ready Draft", edit
        lightly, and send. This is the ONE write action read-only admins are
        allowed to perform.
      </p>
    ),
    cta: { to: "/app/admin/leads#replies", label: "Open Hot Replies →" },
  },
  {
    id: "set_weekly_goal",
    icon: Target,
    title: "Commit to your first-week goals",
    time: "1 min",
    body: (
      <div className="text-sm text-neutral-300 mt-3 space-y-2">
        <div>Your week 1 goals:</div>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Review 50 leads</strong> · get comfortable navigating the pipeline</li>
          <li><strong>Reply to 10 hot leads</strong> · using approved templates only</li>
          <li><strong>Ask 3 questions in Slack</strong> · every "did I do this right?" saves a mistake</li>
        </ul>
        <div className="text-xs text-neutral-500 pt-2">Tick this box once you've read + agreed to the goals.</div>
      </div>
    ),
    cta: null,
  },
];

export default function VAOnboardingPage() {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyStep, setBusyStep] = useState(null);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/va-onboarding/progress");
      setProgress(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load VA progress");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const completeStep = async (stepId) => {
    setBusyStep(stepId);
    try {
      const { data } = await client.post("/admin/va-onboarding/complete-step", {
        step_id: stepId,
      });
      setProgress((prev) => ({
        ...prev,
        steps_completed: Array.from(new Set([...(prev?.steps_completed || []), stepId])),
        completed_count: data.completed_count,
        percent: data.percent,
        completed_at: data.completed_at || prev?.completed_at,
      }));
      if (data.was_new) {
        toast.success(`✓ ${STEPS.find((s) => s.id === stepId)?.title || "Step"} done`);
      }
      if (data.percent === 100 && data.was_new) {
        toast.success("🎉 VA onboarding complete — you're cleared to run outreach!");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save progress");
    } finally {
      setBusyStep(null);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset all onboarding progress? You'll start over from step 1.")) return;
    setResetting(true);
    try {
      await client.post("/admin/va-onboarding/reset");
      await load();
      toast.success("Progress reset");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const doneSet = new Set(progress?.steps_completed || []);
  const percent = progress?.percent ?? 0;
  const allDone = percent === 100;

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 space-y-6" data-testid="va-onboarding-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#FF9764] font-semibold mb-2">
            <Rocket size={14} weight="fill" /> Delegate to VA
          </div>
          <h1 className="text-3xl font-semibold text-white tracking-tight">
            Your cold-outreach onboarding
          </h1>
          <p className="text-sm text-neutral-400 mt-2 max-w-2xl">
            Six short steps to get a new virtual assistant (or read-only admin) safely running cold outreach. Do them in order — most people finish in under 25 minutes.
          </p>
        </div>
        {progress?.started_at && (
          <button
            onClick={reset}
            disabled={resetting}
            data-testid="va-onboarding-reset-btn"
            className="text-xs text-neutral-500 hover:text-white flex items-center gap-1.5 border border-white/10 hover:border-white/25 rounded-full px-3 py-1.5 disabled:opacity-40"
          >
            <ArrowClockwise size={12} weight="bold" /> Reset
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div
        className="card-surface p-5 rounded-xl border border-white/10"
        data-testid="va-onboarding-progress-card"
      >
        <div className="flex items-center justify-between text-sm mb-2">
          <div className="text-white font-medium">
            {progress?.completed_count ?? 0} of {progress?.total_steps ?? STEPS.length} steps done
          </div>
          <div
            className={`text-xs font-semibold ${allDone ? "text-emerald-400" : "text-neutral-400"}`}
            data-testid="va-onboarding-percent"
          >
            {percent}%
          </div>
        </div>
        <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${allDone ? "bg-emerald-400" : "bg-[#FF5A1F]"}`}
            style={{ width: `${percent}%` }}
          />
        </div>
        {allDone && (
          <div className="mt-3 text-xs text-emerald-400 font-medium" data-testid="va-onboarding-complete-banner">
            🎉 Onboarding complete — you're cleared to run outreach.
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500 py-10 text-center">Loading progress…</div>
      ) : (
        <div className="space-y-3">
          {STEPS.map((step, i) => {
            const done = doneSet.has(step.id);
            const Icon = step.icon;
            return (
              <div
                key={step.id}
                data-testid={`va-step-${step.id}`}
                className={`card-surface rounded-xl border transition-colors p-5 ${done ? "border-emerald-500/25 bg-emerald-500/[0.02]" : "border-white/10 hover:border-white/20"}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`shrink-0 mt-0.5 w-10 h-10 rounded-full flex items-center justify-center ${done ? "bg-emerald-500/15 text-emerald-400" : "bg-white/[0.06] text-neutral-400"}`}>
                    <Icon size={20} weight={done ? "fill" : "regular"} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] tracking-widest uppercase text-neutral-500">Step {i + 1} · {step.time}</span>
                    </div>
                    <h3 className={`font-semibold text-base mt-0.5 ${done ? "text-emerald-300" : "text-white"}`}>
                      {step.title}
                    </h3>
                    {step.body}

                    <div className="flex items-center gap-3 mt-4 flex-wrap">
                      {step.cta && (
                        <Link
                          to={step.cta.to}
                          data-testid={`va-step-cta-${step.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#FF9764] hover:text-white bg-[#FF5A1F]/10 hover:bg-[#FF5A1F]/20 border border-[#FF5A1F]/30 rounded-full px-3 py-1.5 transition-colors"
                        >
                          {step.cta.label} <ArrowSquareOut size={11} weight="bold" />
                        </Link>
                      )}
                      <button
                        onClick={() => completeStep(step.id)}
                        disabled={done || busyStep === step.id}
                        data-testid={`va-step-complete-${step.id}`}
                        className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 transition-colors ${done ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 cursor-default" : "bg-white/[0.04] hover:bg-white/[0.10] text-neutral-300 hover:text-white border border-white/15"} disabled:opacity-60`}
                      >
                        {done ? (
                          <>
                            <CheckCircle size={12} weight="fill" /> Done
                          </>
                        ) : (
                          <>
                            <Circle size={12} weight="regular" /> Mark done
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer notes */}
      <div className="text-[11px] text-neutral-600 pt-4 border-t border-white/5">
        Signed in as <span className="text-neutral-400">{user?.email}</span>
        {user?.is_readonly_admin && (
          <span className="ml-2 inline-block px-2 py-0.5 rounded-full uppercase tracking-widest bg-purple-500/15 text-purple-300 border border-purple-500/30">Read-only</span>
        )}
      </div>
    </div>
  );
}
